// src/pages/locations/components/form/FormSection.tsx
import React from "react";

/**
 * One titled group of fields. Sections give the form a readable shape:
 * what the record *is* first, optional tuning after.
 */
export const FormSection: React.FC<{
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, description, children, className = "" }) => (
  <section className={`border-b border-gray-100 px-5 py-5 last:border-b-0 sm:px-6 ${className}`}>
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {description && (
        <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{description}</p>
      )}
    </div>

    <div className="space-y-4">{children}</div>
  </section>
);

/** Collapsible section for fields most people never need to touch. */
export const AdvancedSection: React.FC<{
  title?: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title = "Advanced", description, children }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <section className="border-b border-gray-100 px-5 py-4 last:border-b-0 sm:px-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span>
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {description && (
            <span className="mt-0.5 block text-xs text-gray-500">{description}</span>
          )}
        </span>
        <span className="text-xs font-medium text-sky-700">
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && <div className="mt-4 space-y-4">{children}</div>}
    </section>
  );
};

/** Two fields side by side on desktop, stacked on mobile. */
export const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid gap-4 sm:grid-cols-2">{children}</div>
);
